import type { IndustryId, Quote, QuoteInput } from '@shared/types'
import type { PaymentProvider, PlanChangePreview } from '@shared/payments'
import type { IntegrationDefinition } from '@shared/integrations'
import type { RuleProblem } from '@shared/workflows'
import type { AutoReplyConfig } from '@shared/autoReply'
import type { Operator } from '@shared/segments'
import type { AutomationAction, AutomationTrigger } from '@shared/automationRules'
import type { MessageMediaType } from '@shared/media'
import type { PresenceReading } from '@shared/chatPresence'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues?: unknown,
    /** The whole parsed error body. Some 4xx responses carry a machine-readable next step
     *  alongside the prose — a required addon, or the workspace to bounce a login to — and
     *  reading it off `message` with a regex is how that quietly breaks on a copy edit. */
    public body?: Record<string, unknown>,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })
  const text = await res.text()
  const data = text ? JSON.parse(text) : null
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? 'Request failed.', data?.issues, data ?? undefined)
  }
  return data as T
}

export interface SessionResponse {
  user: { id: string; email: string; name: string } | null
  /** How this browser signed in. Decides what the account screen must ask for before it will
   *  set a new password. Absent on the signed-out response. */
  authMethod?: 'password' | 'code' | 'sso'
  workspace: {
    id: string
    name: string
    slug: string
    industryId: string
    localeId: string
    onboardingStep: string
    onboardingCompletedAt: string | null
    /** This session's role in this workspace — an invited teammate is rarely the owner. */
    role: 'owner' | 'admin' | 'agent' | 'viewer'
    /** True only for the shared public preview workspace, never for a real customer's own
     *  viewer-role teammates — it drives the demo banner and disables the composer. */
    isDemo: boolean
  } | null
}

export interface WorkspacesResponse {
  workspaces: {
    id: string
    name: string
    slug: string
    industryId: string
    role: 'owner' | 'admin' | 'agent' | 'viewer'
  }[]
  activeWorkspaceId: string
}

export interface WorkspaceResponse {
  workspace: SessionResponse['workspace']
  subscription: {
    id: string
    planId: string
    billingCycle: 'monthly' | 'annual'
    status: string
    seats: number
    numbers: number
    conversations: number
    addons: string[]
    currency: string
    totalMinor: number
    provider: PaymentProvider | null
    providerSubscriptionId: string | null
    paymentStatus: 'unpaid' | 'paid' | 'failed'
    cancelAtPeriodEnd: boolean
    canceledAt: string | null
    trialEndsAt: string | null
    currentPeriodEnd: string | null
  } | null
  numbers: {
    id: string
    label: string
    phone: string
    status: string
    pairingQr: string | null
    sessionStatus: string | null
    sessionDetail: string | null
    historyState: 'importing' | 'done' | null
    historyImported: number
    historyTotal: number
    historySyncedAt: string | null
    lastConnectedAt: string | null
    /** When this number first paired, which is where the warm-up ramp counts from. */
    connectedAt: string | null
    /** Its recent bulk-send outcomes, so the numbers screen scores health as the server does. */
    recentSends: { attempted: number; failed: number } | null
  }[]
  invoices: {
    id: string
    amountMinor: number
    currency: string
    status: 'paid' | 'failed'
    periodStart: string | null
    periodEnd: string | null
    createdAt: string
  }[]
  billing: {
    country: string | null
    provider: PaymentProvider
    regionOk: boolean
    regionReason: string | null
    canResume: boolean
    /** A card processor is wired for this region's provider; false → checkout is not yet open. */
    enabled: boolean
  }
  /** Where the subscription stands and which paid modules are live — the same verdict the server
   *  enforces with, so the UI can prompt to upgrade instead of surfacing a bare 402. */
  access: {
    active: boolean
    reason: 'active' | 'trial' | 'trial_expired' | 'past_due' | 'canceled' | 'inactive'
    trialDaysLeft?: number
    features: string[]
  } | null
  /** Same predicate as `billing.enabled`, but visible to every role (not just owner/admin) — the
   *  trial banner shows to everyone, so it needs this to avoid telling an agent or viewer to "add
   *  a payment method" on a workspace where billing isn't wired up at all. */
  billingEnabled: boolean
  quote: Quote | null
  /** The first-run checklist's three items — connect a number, invite the team, send a real
   *  reply. Visible to every role, same reasoning as `access`. */
  checklist: {
    numberConnected: boolean
    teamInvited: boolean
    firstReplySent: boolean
  }
}

export interface GeoResponse {
  country: string | null
  provider: PaymentProvider
  publishableKey: string | null
}

export type CheckoutResponse =
  | {
      provider: 'razorpay'
      mode: 'subscription'
      subscriptionId: string
      keyId: string
      amountMinor: number
      currency: string
      name: string
      description: string
      prefill: { name: string; email: string }
    }
  | {
      provider: 'stripe'
      mode: 'subscription'
      checkoutUrl: string
      amountMinor: number
      currency: string
    }

export type TopupOrderResponse =
  | {
      provider: 'razorpay'
      mode: 'order'
      orderId: string
      credits: number
      keyId: string
      amountMinor: number
      currency: string
      name: string
      description: string
      prefill: { name: string; email: string }
    }
  | {
      provider: 'stripe'
      mode: 'order'
      checkoutUrl: string
      credits: number
      amountMinor: number
      currency: string
    }

export const api = {
  session: () => request<SessionResponse>('/auth/session'),

  signup: (body: { name: string; email: string; password: string; company: string; inviteToken?: string }) =>
    request<{ user: SessionResponse['user']; joinedWorkspace?: boolean }>('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Which ways in this deployment can offer: false means no mail transport, so no emailed codes. */
  authMethods: () => request<{ emailCode: boolean }>('/auth/methods'),

  /** Emailed-code sign-up: step one mails a code, step two creates the account. No password. */
  requestSignupCode: (body: { name: string; email: string; company: string; inviteToken?: string }) =>
    request<{ ok: true }>('/auth/signup/code', { method: 'POST', body: JSON.stringify(body) }),

  verifySignup: (body: { name: string; email: string; company: string; code: string; inviteToken?: string }) =>
    request<{ user: SessionResponse['user']; joinedWorkspace?: boolean }>('/auth/signup/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  login: (body: { email: string; password: string }) =>
    request<{ user: SessionResponse['user'] }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Passwordless sign-in. `requestOtp` resolves the same way for an address with an account and
   * one without — the server will not say which, so neither can this. A 503 is the exception,
   * and means no mail transport is configured on the deployment at all.
   */
  requestOtp: (body: { email: string }) =>
    request<{ ok: true }>('/auth/otp/request', { method: 'POST', body: JSON.stringify(body) }),

  verifyOtp: (body: { email: string; code: string }) =>
    request<{ user: SessionResponse['user'] }>('/auth/otp/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Signs the browser into the shared, read-only public demo workspace — no form, no account. */
  startDemo: () => request<{ ok: true }>('/demo/start', { method: 'POST' }),

  inviteDetails: (token: string) =>
    request<{ email: string; role: string; workspaceName: string }>(`/invite/${token}`),

  acceptInvite: (token: string) =>
    request<{ ok: true; workspaceId: string }>(`/invite/${token}/accept`, { method: 'POST' }),

  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  changePassword: (input: { currentPassword?: string; newPassword: string }) =>
    request<{ ok: true }>('/auth/password', { method: 'POST', body: JSON.stringify(input) }),

  quote: (input: QuoteInput) =>
    request<Quote>('/quote', { method: 'POST', body: JSON.stringify(input) }),

  saveLeadQuote: (input: QuoteInput, email?: string) =>
    request<{ ok: true; quote: Quote }>('/lead-quote', {
      method: 'POST',
      body: JSON.stringify({ input, email }),
    }),

  setStep: (step: string, industryId?: IndustryId) =>
    request<{ ok: true }>('/onboarding/step', {
      method: 'PATCH',
      body: JSON.stringify({ step, industryId }),
    }),

  completeOnboarding: (body: { industryId: string; localeId: string; config: QuoteInput }) =>
    request<{ ok: true; quote: Quote; trialEndsAt: string }>('/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  workspace: () => request<WorkspaceResponse>('/workspace'),

  workspaces: () => request<WorkspacesResponse>('/workspaces'),

  switchWorkspace: (workspaceId: string) =>
    request<{ ok: true }>('/session/workspace', {
      method: 'POST',
      body: JSON.stringify({ workspaceId }),
    }),

  updateSubscription: (input: QuoteInput, prorationDate?: number) =>
    request<{ ok: true; quote: Quote; change: PlanChangePreview }>('/workspace/subscription', {
      method: 'PUT',
      // Echoing the previewed instant makes the charge match the figure the customer saw.
      body: JSON.stringify(prorationDate ? { ...input, prorationDate } : input),
    }),

  previewSubscriptionChange: (input: QuoteInput) =>
    request<{ preview: PlanChangePreview; quote: Quote }>('/subscription/preview', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  addNumber: (body: { label: string; phone: string }) =>
    request<{ ok: true; id: string }>('/workspace/numbers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  removeNumber: (id: string) =>
    request<{ ok: true }>(`/workspace/numbers/${id}`, { method: 'DELETE' }),

  numberLabels: (numberId: string) =>
    request<{
      labels: { id: string; name: string; color: string }[]
      available: { id: string; name: string; color: string }[]
      canManage: boolean
    }>(`/workspace/numbers/${numberId}/labels`),

  setNumberLabels: (numberId: string, names: string[]) =>
    request<{ ok: true }>(`/workspace/numbers/${numberId}/labels`, {
      method: 'PUT',
      body: JSON.stringify({ names }),
    }),

  geo: () => request<GeoResponse>('/geo'),

  checkout: () => request<CheckoutResponse>('/checkout', { method: 'POST' }),

  verifyRazorpay: (body: { subscriptionId: string; paymentId: string; signature: string }) =>
    request<{ ok: true }>('/checkout/razorpay/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  topupOrder: (credits: number) =>
    request<TopupOrderResponse>('/billing/topup/order', {
      method: 'POST',
      body: JSON.stringify({ credits }),
    }),

  // The credited amount is read back from the order Razorpay itself holds, not sent by the
  // client — see the worker route for why that boundary matters.
  topupConfirm: (body: { orderId: string; paymentId: string; signature: string }) =>
    request<{ ok: true; credits: number }>('/billing/topup/confirm', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  cancelSubscription: () =>
    request<{ ok: true; activeUntil: string | null }>('/subscription/cancel', { method: 'POST' }),

  resumeSubscription: () => request<{ ok: true }>('/subscription/resume', { method: 'POST' }),

  billingPortal: () => request<{ url: string }>('/billing-portal', { method: 'POST' }),

  tenant: () => request<TenantResponse>('/tenant'),

  provisionTenant: (
    body:
      | { provider: 'managed' }
      | { provider: 'neon'; apiKey: string; regionId: string }
      | { provider: 'supabase'; connectionString: string },
  ) =>
    request<{ ok: true; region: string | null; projectId: string | null }>('/tenant/provision', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  disclosure: () =>
    request<{
      version: number
      accepted: boolean
      acceptedAt: string | null
      acceptedVersion: number | null
    }>('/disclosure'),

  acceptDisclosure: () =>
    request<{ ok: true; version: number }>('/disclosure/accept', { method: 'POST' }),

  pairNumber: (id: string) =>
    request<{ status: string; qr: string | null }>(`/numbers/${id}/pair`, { method: 'POST' }),

  resetNumber: (id: string) => request<{ ok: true }>(`/numbers/${id}/reset`, { method: 'POST' }),

  restartNumber: (id: string) =>
    request<{ status: string }>(`/numbers/${id}/restart`, { method: 'POST' }),

  inbox: (archived = false) => request<InboxResponse>(`/inbox${archived ? '?archived=1' : ''}`),

  conversation: (id: string) => request<ConversationResponse>(`/inbox/${id}`),

  reply: (
    id: string,
    body: {
      body: string
      isPrivateNote?: boolean
      /** What the upload route handed back; only its key is trusted server side. */
      media?: UploadedMedia | null
      location?: { latitude: number; longitude: number; name?: string } | null
    },
  ) =>
    request<{ ok: true; messageId: string; delivered: boolean }>(`/inbox/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  messageAction: (
    conversationId: string,
    messageId: string,
    body: {
      action: MessageAction
      text?: string
      emoji?: string
      reason?: string
      ticketId?: string
      title?: string
      category?: string
      priority?: 'low' | 'normal' | 'high' | 'urgent'
      assigneeUserId?: string
      dueAt?: string
    },
  ) =>
    request<{ ok: true; ticket?: { id: string; reference: string } }>(
      `/inbox/${conversationId}/messages/${messageId}/actions`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  scheduledMessagesForChat: (conversationId: string) =>
    request<{ messages: ScheduledMessageView[] }>(`/inbox/${conversationId}/scheduled-messages`),

  scheduleMessage: (
    conversationId: string,
    body: {
      body: string
      scheduledFor: string
      recurrence?: 'daily' | 'weekly' | 'monthly' | null
      recurrenceInterval?: number | null
      recurrenceEndAt?: string | null
      recurrenceEndCount?: number | null
      recurrenceWeekdays?: number[] | null
    },
  ) =>
    request<{ ok: true; id: string }>(`/inbox/${conversationId}/scheduled-messages`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateScheduledMessage: (
    id: string,
    body: {
      body?: string
      scheduledFor?: string
      recurrence?: 'daily' | 'weekly' | 'monthly' | null
      recurrenceInterval?: number | null
      recurrenceEndAt?: string | null
      recurrenceEndCount?: number | null
      recurrenceWeekdays?: number[] | null
    },
  ) => request<{ ok: true }>(`/scheduled-messages/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  cancelScheduledMessage: (id: string) =>
    request<{ ok: true }>(`/scheduled-messages/${id}/cancel`, { method: 'POST' }),

  scheduledMessages: () => request<{ messages: ScheduledMessageLogView[] }>('/scheduled-messages'),

  forwardMessage: (conversationId: string, messageId: string, toConversationId: string) =>
    request<{ ok: true; messageId: string }>(`/inbox/${conversationId}/messages/${messageId}/forward`, {
      method: 'POST',
      body: JSON.stringify({ toConversationId }),
    }),

  markRead: (conversationId: string) =>
    request<{ ok: true }>(`/inbox/${conversationId}/read`, { method: 'POST' }),

  conversationLabels: (conversationId: string) =>
    request<{ labels: { id: string; name: string; color: string }[] }>(`/inbox/${conversationId}/labels`),

  bulkInboxAction: (body: {
    action:
      | 'add_label'
      | 'remove_label'
      | 'tag_contact'
      | 'untag_contact'
      | 'assign'
      | 'close'
      | 'archive'
      | 'unarchive'
      | 'mark_read'
      | 'mark_unread'
      | 'apply_csv'
    conversationIds: string[]
    labelId?: string
    tag?: string
    assigneeUserId?: string | null
    /** `assign` only — the team tier above an individual assignee, settable with or without one. */
    teamId?: string | null
    /** `apply_csv` only — one row per chat, matched by phone; `values` keys are property names
     *  (or the literal `labels`, semicolon/comma-separated). */
    rows?: { phone: string; values: Record<string, string> }[]
  }) =>
    request<{ ok: true; updated: number; skipped?: number; unmatched?: number; failures?: { reference: string; error: string }[] }>(
      '/inbox/bulk',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Not a fetch — the browser downloads this URL directly, same as the Exports screen. */
  exportSelectedUrl: (kind: 'conversations' | 'messages' | 'tickets', conversationIds: string[]) =>
    `/api/exports/${kind}?ids=${conversationIds.map(encodeURIComponent).join(',')}`,

  chatProperties: (conversationId: string) =>
    request<{ properties: PropertyView[] }>(`/inbox/${conversationId}/properties`),

  setChatProperty: (conversationId: string, defId: string, value: string | null) =>
    request<{ ok: true }>(`/inbox/${conversationId}/properties/${defId}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),

  ticketProperties: (ticketId: string) =>
    request<{ properties: PropertyView[] }>(`/tickets/${ticketId}/properties`),

  setTicketProperty: (ticketId: string, defId: string, value: string | null) =>
    request<{ ok: true }>(`/tickets/${ticketId}/properties/${defId}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    }),

  closeTicket: (ticketId: string) =>
    request<{ ok: true }>(`/tickets/${ticketId}/close`, { method: 'POST' }),

  updateTicket: (
    ticketId: string,
    body: Partial<{
      title: string
      category: string
      priority: 'low' | 'normal' | 'high' | 'urgent'
      status: 'open' | 'pending' | 'resolved' | 'closed'
      assigneeUserId: string | null
    }>,
  ) => request<{ ok: true; ticket: InboxTicket }>(`/tickets/${ticketId}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteTicket: (ticketId: string) =>
    request<{ ok: true }>(`/tickets/${ticketId}`, { method: 'DELETE' }),

  quickReplies: () =>
    request<{ replies: QuickReplyView[]; canManage: boolean }>('/quick-replies'),

  updateQuickReply: (
    id: string,
    body: Partial<{ title: string; body: string; visibility: 'all' | 'members' | 'roles'; visibilityIds: string[] }>,
  ) =>
    request<{ ok: true }>(`/quick-replies/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  createQuickReply: (body: {
    shortcut: string
    title: string
    body: string
    visibility?: 'all' | 'members' | 'roles'
    visibilityIds?: string[]
  }) =>
    request<{ ok: true; reply: QuickReplyView }>('/quick-replies', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  deleteQuickReply: (id: string) =>
    request<{ ok: true }>(`/quick-replies/${id}`, { method: 'DELETE' }),

  metrics: (filters?: {
    dateFrom?: string
    dateTo?: string
    labelIds?: string[]
    kind?: string[]
    assigneeUserIds?: string[]
    match?: 'all' | 'any'
    /** One entry per custom-property def with at least one value selected. */
    propertyFilters?: Record<string, string[]>
  }) => {
    const qs = new URLSearchParams()
    if (filters?.dateFrom) qs.set('dateFrom', filters.dateFrom)
    if (filters?.dateTo) qs.set('dateTo', filters.dateTo)
    if (filters?.labelIds?.length) qs.set('labelIds', filters.labelIds.join(','))
    if (filters?.kind?.length) qs.set('kind', filters.kind.join(','))
    if (filters?.assigneeUserIds?.length) qs.set('assigneeUserIds', filters.assigneeUserIds.join(','))
    if (filters?.match) qs.set('match', filters.match)
    const propertyEntries = Object.entries(filters?.propertyFilters ?? {}).filter(([, v]) => v.length > 0)
    if (propertyEntries.length > 0) {
      qs.set(
        'propertyFilters',
        propertyEntries
          .map(([defId, values]) => `${defId}:${values.map(encodeURIComponent).join('|')}`)
          .join(','),
      )
    }
    const query = qs.toString()
    return request<MetricsResponse>(`/metrics${query ? `?${query}` : ''}`)
  },

  broadcasts: () => request<BroadcastsResponse>('/broadcasts'),

  saveBroadcastTemplate: (body: { name: string; body: string; media: UploadedMedia | null }) =>
    request<{ ok: true }>('/broadcast-templates', { method: 'POST', body: JSON.stringify(body) }),

  deleteBroadcastTemplate: (id: string) =>
    request<{ ok: true }>(`/broadcast-templates/${id}`, { method: 'DELETE' }),

  previewBroadcast: (body: {
    body: string
    variables: { name: string; fallback: string }[]
    recipients?: { remoteId: string; variables?: Record<string, string> }[]
    /** When set, the audience is resolved server-side and `recipients` is ignored. */
    segmentId?: string
    /** Which connected numbers to spread this send across. Omit or empty for every one. */
    numberIds?: string[]
  }) => request<BroadcastPreview>('/broadcasts/preview', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  createBroadcast: (body: {
    name: string
    body: string
    variables: { name: string; fallback: string }[]
    recipients?: { remoteId: string; displayName?: string; variables?: Record<string, string> }[]
    segmentId?: string
    scheduledFor?: string | null
    recurrence?: string | null
    recurrenceInterval?: number | null
    recurrenceEndAt?: string | null
    recurrenceEndCount?: number | null
    recurrenceWeekdays?: number[] | null
    media?: UploadedMedia | null
    numberIds?: string[]
  }) => request<{ ok: true; id: string; scheduled: number; deferred: number; unreachable: number }>('/broadcasts', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  /**
   * Asks WhatsApp which of an audience actually has an account.
   *
   * Its own call rather than part of the preview on purpose: the preview refires as the
   * customer types, and this one reaches the WhatsApp socket.
   */
  checkReachability: (body: {
    recipients?: { remoteId: string }[]
    segmentId?: string
    numberIds?: string[]
  }) => request<ReachabilityReport & { checked: number; remaining: number }>(
    '/broadcasts/reachability',
    { method: 'POST', body: JSON.stringify(body) },
  ),

  /**
   * Uploads an attachment.
   *
   * Sent as raw bytes rather than multipart: there is exactly one file, the name travels in
   * a header, and this avoids building a multipart parser in the worker for no benefit.
   */
  uploadMedia: async (file: File): Promise<UploadedMedia> => {
    const res = await fetch('/api/media', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-File-Name': encodeHeader(file.name),
      },
      body: file,
    })
    const text = await res.text()
    const data = text ? JSON.parse(text) : null
    if (!res.ok) throw new ApiError(res.status, data?.error ?? 'The file could not be uploaded.')
    return data as UploadedMedia
  },

  broadcast: (id: string) => request<BroadcastDetail>(`/broadcasts/${id}`),

  saveList: (body: { name: string; entries: { remoteId: string; displayName?: string }[] }) =>
    request<{ ok: true }>('/saved-lists', { method: 'POST', body: JSON.stringify(body) }),

  cancelBroadcast: (id: string) =>
    request<{ ok: true }>(`/broadcasts/${id}/cancel`, { method: 'POST' }),

  retryFailedBroadcast: (id: string) =>
    request<{ ok: true; requeued: number }>(`/broadcasts/${id}/retry-failed`, { method: 'POST' }),

  duplicateBroadcastSource: (id: string) => request<DuplicateBroadcastSource>(`/broadcasts/${id}/duplicate-source`),

  propertyDefs: () =>
    request<{ defs: PropertyDefView[]; sections: PropertySectionView[]; canManage: boolean }>('/property-defs'),

  createPropertyDef: (body: {
    entity: 'chat' | 'ticket'
    name: string
    section: string
    type: string
    options: string[]
    cascadeOptions?: string[][]
    hint: string
    requiredBeforeClose: boolean
  }) => request<{ ok: true }>('/property-defs', { method: 'POST', body: JSON.stringify(body) }),

  updatePropertyDef: (
    id: string,
    body: {
      requiredBeforeClose?: boolean
      hint?: string
      name?: string
      section?: string
      options?: string[]
      cascadeOptions?: string[][]
    },
  ) => request<{ ok: true }>(`/property-defs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deletePropertyDef: (id: string) =>
    request<{ ok: true }>(`/property-defs/${id}`, { method: 'DELETE' }),

  savePropertySections: (body: { entity: 'chat' | 'ticket'; names: string[]; renames?: Record<string, string> }) =>
    request<{ ok: true }>('/property-sections', { method: 'PUT', body: JSON.stringify(body) }),

  autoReply: () => request<AutoReplyResponse>('/auto-reply'),

  saveAutoReply: (config: unknown) =>
    request<{ ok: true }>('/auto-reply', { method: 'PUT', body: JSON.stringify({ config }) }),

  previewAutoReply: (body: {
    body: string
    kind?: 'direct' | 'group'
    isFirstEverMessage?: boolean
    at?: string
    config?: unknown
  }) =>
    request<{ message: string | null; kind: string | null; reason: string | null; open: boolean; pending?: boolean }>(
      '/auto-reply/preview',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  knowledge: () => request<KnowledgeResponse>('/knowledge'),

  createAnswer: (body: KnowledgeInput) =>
    request<{ ok: true; id: string; indexed: boolean }>('/knowledge', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateAnswer: (id: string, body: KnowledgeInput) =>
    request<{ ok: true; indexed: boolean }>(`/knowledge/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteAnswer: (id: string) => request<{ ok: true }>(`/knowledge/${id}`, { method: 'DELETE' }),

  /** Turns an uploaded document into knowledge chunks — see `POST /api/knowledge/documents`. */
  addKnowledgeDocument: (media: UploadedMedia) =>
    request<{ ok: true; chunks: number; indexed: number }>('/knowledge/documents', {
      method: 'POST',
      body: JSON.stringify({ media }),
    }),

  deleteKnowledgeDocument: (documentKey: string) =>
    request<{ ok: true }>('/knowledge/documents', {
      method: 'DELETE',
      body: JSON.stringify({ documentKey }),
    }),

  knowledgeConnectors: () =>
    request<{ notion: { configured: boolean }; confluence: { configured: boolean }; canManage: boolean }>(
      '/knowledge/connectors',
    ),

  connectNotion: (token: string) =>
    request<{ ok: true }>('/knowledge/connectors/notion', { method: 'PUT', body: JSON.stringify({ token }) }),

  connectConfluence: (siteUrl: string, email: string, apiToken: string) =>
    request<{ ok: true }>('/knowledge/connectors/confluence', {
      method: 'PUT',
      body: JSON.stringify({ siteUrl, email, apiToken }),
    }),

  disconnectKnowledgeSource: (source: 'notion' | 'confluence') =>
    request<{ ok: true }>(`/knowledge/connectors/${source}`, { method: 'DELETE' }),

  syncKnowledgePage: (source: 'notion' | 'confluence', pageUrl: string) =>
    request<{ ok: true; title: string; chunks: number; indexed: number }>('/knowledge/sync', {
      method: 'POST',
      body: JSON.stringify({ source, pageUrl }),
    }),

  reindexKnowledge: () =>
    request<{ ok: true; indexed: number; model?: string }>('/knowledge/reindex', { method: 'POST' }),

  testEmbeddingModel: () =>
    request<{ ok: true; model: string; dimensions: number; url: string }>('/knowledge/model/test', {
      method: 'POST',
    }),

  askKnowledge: (question: string, record = false, conversationId?: string) =>
    request<KnowledgeAnswer>('/knowledge/answer', {
      method: 'POST',
      body: JSON.stringify({ question, record, conversationId }),
    }),

  aiSettings: () => request<AiSettingsView>('/ai/settings'),

  saveAiSettings: (body: { apiKey?: string; baseUrl?: string; model?: string }) =>
    request<{ ok: true }>('/ai/settings', { method: 'PUT', body: JSON.stringify(body) }),

  clearAiSettings: () => request<{ ok: true }>('/ai/settings', { method: 'DELETE' }),

  summarizeConversation: (conversationId: string) =>
    request<{ ok: true; summary: string }>(`/inbox/${conversationId}/ai/summarize`, { method: 'POST' }),

  translateMessage: (conversationId: string, text: string, targetLanguage?: string) =>
    request<{ ok: true; translated: string }>(`/inbox/${conversationId}/ai/translate`, {
      method: 'POST',
      body: JSON.stringify({ text, targetLanguage }),
    }),

  rewriteMessage: (conversationId: string, text: string, style: RewriteStyle) =>
    request<{ ok: true; rewritten: string }>(`/inbox/${conversationId}/ai/rewrite`, {
      method: 'POST',
      body: JSON.stringify({ text, style }),
    }),

  setAiResponderState: (conversationId: string, state: 'active' | 'inactive') =>
    request<{ ok: true }>(`/inbox/${conversationId}/ai-responder`, {
      method: 'PUT',
      body: JSON.stringify({ state }),
    }),

  auditLog: (filter?: { action?: string; actorUserId?: string; limit?: number }) => {
    const qs = new URLSearchParams()
    if (filter?.action) qs.set('action', filter.action)
    if (filter?.actorUserId) qs.set('actorUserId', filter.actorUserId)
    if (filter?.limit) qs.set('limit', String(filter.limit))
    const query = qs.toString()
    return request<AuditLogResponse>(`/audit-log${query ? `?${query}` : ''}`)
  },

  verifyAuditLog: () => request<AuditLogVerification>('/audit-log/verify'),

  auditLogSettings: () => request<{ retentionDays: number | null }>('/audit-log/settings'),

  saveAuditLogRetention: (retentionDays: number | null) =>
    request<{ ok: true }>('/audit-log/settings', { method: 'PUT', body: JSON.stringify({ retentionDays }) }),

  ssoSettings: () => request<SsoSettingsView>('/sso/settings'),

  saveSsoSettings: (body: { issuer: string; clientId: string; clientSecret?: string; allowedDomain: string; enabled: boolean }) =>
    request<{ ok: true; domainVerificationRecord: string; domainVerified: boolean }>('/sso/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  verifySsoDomain: () => request<{ ok: true; verifiedAt: string }>('/sso/verify-domain', { method: 'POST' }),

  disconnectSso: () => request<{ ok: true }>('/sso/settings', { method: 'DELETE' }),

  rotateScimToken: () => request<{ token: string; displayHint: string }>('/sso/scim-token', { method: 'POST' }),

  revokeScimToken: () => request<{ ok: true }>('/sso/scim-token', { method: 'DELETE' }),

  reactionRules: () => request<ReactionRulesResponse>('/reaction-rules'),

  saveReactionRules: (rules: { emoji: string; action: string; value?: string; enabled: boolean }[]) =>
    request<{ ok: true }>('/reaction-rules', { method: 'PUT', body: JSON.stringify({ rules }) }),

  groups: (numberId?: string) =>
    request<GroupsResponse>(`/groups${numberId ? `?numberId=${numberId}` : ''}`),

  group: (jid: string, numberId?: string) =>
    request<GroupDetailResponse>(
      `/groups/${encodeURIComponent(jid)}${numberId ? `?numberId=${numberId}` : ''}`,
    ),

  groupAction: (jid: string, body: GroupActionInput) =>
    request<{ ok?: true; link?: string | null; results?: { jid: string; status: string }[] }>(
      `/groups/${encodeURIComponent(jid)}/actions`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  createGroup: (body: {
    subject: string
    participants: string[]
    numberId?: string
    blueprintId?: string
  }) =>
    request<{ group: GroupDetail; blueprint?: CreatedFromBlueprint }>('/groups', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  groupBlueprints: () =>
    request<{ industryId: string; blueprints: GroupBlueprint[] }>('/group-blueprints'),

  groupAnalytics: (jid: string, numberId?: string) =>
    request<GroupAnalyticsResponse>(
      `/groups/${encodeURIComponent(jid)}/analytics${numberId ? `?numberId=${numberId}` : ''}`,
    ),

  groupsHealth: () => request<GroupsHealthResponse>('/groups/health'),

  polls: (conversationId: string) =>
    request<PollsResponse>(`/polls?conversationId=${encodeURIComponent(conversationId)}`),

  createPoll: (body: {
    conversationId: string
    question: string
    options: string[]
    selectableCount: number
  }) => request<{ ok: true; pollId: string; asked: number }>('/polls', {
    method: 'POST',
    body: JSON.stringify(body),
  }),

  closePoll: (id: string) =>
    request<{ ok: true }>(`/polls/${id}/close`, { method: 'POST' }),

  workflowRules: () => request<WorkflowRulesResponse>('/workflow-rules'),

  createWorkflowRule: (body: WorkflowRuleInput) =>
    request<{ ok: true; id: string }>('/workflow-rules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateWorkflowRule: (id: string, body: WorkflowRuleInput) =>
    request<{ ok: true }>(`/workflow-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteWorkflowRule: (id: string) =>
    request<{ ok: true }>(`/workflow-rules/${id}`, { method: 'DELETE' }),

  reorderWorkflowRules: (ids: string[]) =>
    request<{ ok: true }>('/workflow-rules/reorder', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  automationRules: () => request<AutomationRulesResponse>('/automation-rules'),

  createAutomationRule: (body: AutomationRuleInput) =>
    request<{ ok: true; id: string }>('/automation-rules', { method: 'POST', body: JSON.stringify(body) }),

  updateAutomationRule: (id: string, body: Partial<AutomationRuleInput>) =>
    request<{ ok: true }>(`/automation-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteAutomationRule: (id: string) =>
    request<{ ok: true }>(`/automation-rules/${id}`, { method: 'DELETE' }),

  reorderAutomationRules: (ids: string[]) =>
    request<{ ok: true }>('/automation-rules/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),

  automationRuleRuns: (id: string) => request<{ runs: AutomationRuleRun[] }>(`/automation-rules/${id}/runs`),

  automationFlows: () => request<AutomationFlowsResponse>('/automation-flows'),

  createAutomationFlow: (body: AutomationFlowInput) =>
    request<{ ok: true; id: string }>('/automation-flows', { method: 'POST', body: JSON.stringify(body) }),

  updateAutomationFlow: (id: string, body: Partial<AutomationFlowInput>) =>
    request<{ ok: true }>(`/automation-flows/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteAutomationFlow: (id: string) => request<{ ok: true }>(`/automation-flows/${id}`, { method: 'DELETE' }),

  products: () => request<ProductsResponse>('/products'),

  createProduct: (body: ProductInput) =>
    request<{ ok: true; id: string }>('/products', { method: 'POST', body: JSON.stringify(body) }),

  updateProduct: (id: string, body: Partial<ProductInput>) =>
    request<{ ok: true }>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteProduct: (id: string) => request<{ ok: true }>(`/products/${id}`, { method: 'DELETE' }),

  createProductPaymentLink: (id: string, conversationId?: string) =>
    request<{ ok: true; url: string; provider: 'stripe' | 'razorpay'; saleId: string }>(`/products/${id}/payment-link`, {
      method: 'POST',
      body: JSON.stringify({ conversationId }),
    }),

  integrations: () => request<IntegrationsResponse>('/integrations'),

  saveIntegration: (
    provider: string,
    body: { credentials: Record<string, string>; options: Record<string, string>; events: string[] },
  ) =>
    request<{ ok: true; detail: string }>(`/integrations/${provider}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  testIntegration: (provider: string) =>
    request<{ ok: true; detail: string }>(`/integrations/${provider}/test`, { method: 'POST' }),

  retryIntegration: (provider: string) =>
    request<{ ok: true; requeued: number }>(`/integrations/${provider}/retry`, { method: 'POST' }),

  disconnectIntegration: (provider: string) =>
    request<{ ok: true }>(`/integrations/${provider}`, { method: 'DELETE' }),

  inboundWebhook: () => request<InboundWebhookSettings>('/settings/inbound-webhook'),

  rotateInboundWebhookKey: () =>
    request<{ token: string; displayHint: string; url: string }>('/settings/inbound-webhook/rotate', {
      method: 'POST',
    }),

  team: () => request<TeamResponse>('/team'),

  createTeamGroup: (body: { name: string; memberIds?: string[] }) =>
    request<{ ok: true; id: string }>('/team/teams', { method: 'POST', body: JSON.stringify(body) }),

  updateTeamGroup: (id: string, body: { name?: string; memberIds?: string[] }) =>
    request<{ ok: true }>(`/team/teams/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteTeamGroup: (id: string) => request<{ ok: true }>(`/team/teams/${id}`, { method: 'DELETE' }),

  invite: (body: { email: string; role: string; labelIds: string[] }) =>
    request<{ ok: true; inviteUrl: string; email: string; emailed: boolean }>('/team/invite', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  revokeInvite: (id: string) =>
    request<{ ok: true }>(`/team/invitations/${id}/revoke`, { method: 'POST' }),

  updateAccess: (userId: string, body: { role?: string; labelIds: string[]; numberIds?: string[] }) =>
    request<{ ok: true }>(`/team/members/${userId}/access`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  removeMember: (userId: string) =>
    request<{ ok: true }>(`/team/members/${userId}`, { method: 'DELETE' }),

  createLabel: (body: { name: string; color: string; scope?: 'chat' | 'number'; description?: string }) =>
    request<{ ok: true; label: LabelView }>('/team/labels', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateLabel: (id: string, body: Partial<{ name: string; color: string; description: string }>) =>
    request<{ ok: true }>(`/team/labels/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteLabel: (id: string) => request<{ ok: true }>(`/team/labels/${id}`, { method: 'DELETE' }),

  ticketStatuses: () => request<TicketStatusesView>('/ticket-statuses'),

  saveTicketStatuses: (statuses: { kind: string; label: string; color: string }[]) =>
    request<{ ok: true }>('/ticket-statuses', { method: 'PUT', body: JSON.stringify({ statuses }) }),

  setPrivacy: (body: {
    maskNumbers: boolean
    revealDeletedMessages?: boolean
    restrictOutgoingNumber?: boolean
    hideAgentName?: boolean
  }) =>
    request<{ ok: true; maskNumbers: boolean; revealDeletedMessages: boolean; restrictOutgoingNumber: boolean; hideAgentName: boolean }>(
      '/team/privacy',
      { method: 'PUT', body: JSON.stringify(body) },
    ),

  setPresence: (status: 'online' | 'offline') =>
    request<{ ok: true; status: string }>('/team/presence', {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  setPresenceBulk: (userIds: string[], status: 'online' | 'offline') =>
    request<{ ok: true; updated: number }>('/team/presence/bulk', {
      method: 'POST',
      body: JSON.stringify({ userIds, status }),
    }),

  updateShifts: (userId: string, shifts: ShiftSlotView[]) =>
    request<{ ok: true; shifts: number }>(`/team/members/${userId}/shifts`, {
      method: 'PUT',
      body: JSON.stringify({ shifts }),
    }),

  /**
   * Whether the customer is at their phone. Polled while a thread is open — the first call
   * for a chat only subscribes, so an answer arrives on a later one if it arrives at all.
   */
  conversationPresence: (conversationId: string) =>
    request<{ presence: PresenceReading }>(`/inbox/${conversationId}/presence`),

  conversationVisibility: (conversationId: string) =>
    request<{ count: number; members: { userId: string; name: string }[] }>(
      `/conversations/${conversationId}/visibility`,
    ),

  agents: () => request<AgentsResponse>('/agents'),

  createAgent: (body: {
    name: string
    scopes: string[]
    unrestricted: boolean
    labelIds: string[]
  }) => request<NewAgentCredential>('/agents', { method: 'POST', body: JSON.stringify(body) }),

  updateAgentAccess: (id: string, body: { unrestricted: boolean; labelIds: string[] }) =>
    request<{ ok: true }>(`/agents/${id}/access`, { method: 'PUT', body: JSON.stringify(body) }),

  revokeAgent: (id: string) => request<{ ok: true }>(`/agents/${id}/revoke`, { method: 'POST' }),

  agentActivity: () => request<{ actions: AgentAction[] }>('/agents/activity'),

  agentSendSettings: () =>
    request<{ settings: AgentSendSettings; open: boolean; nextOpensAt: string | null }>('/agent-outreach/settings'),

  saveAgentSendSettings: (body: AgentSendSettings) =>
    request<{ ok: true; settings: AgentSendSettings }>('/agent-outreach/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  segments: () => request<SegmentsResponse>('/segments'),

  createSegment: (body: { name: string; entity: 'contact' | 'conversation'; match: 'all' | 'any'; conditions: SegmentConditionEntry[] }) =>
    request<{ ok: true; id: string }>('/segments', { method: 'POST', body: JSON.stringify(body) }),

  updateSegment: (id: string, body: { name?: string; match?: 'all' | 'any'; conditions?: SegmentConditionEntry[] }) =>
    request<{ ok: true }>(`/segments/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteSegment: (id: string) => request<{ ok: true }>(`/segments/${id}`, { method: 'DELETE' }),

  previewSegment: (body: { entity: 'contact' | 'conversation'; match: 'all' | 'any'; conditions: SegmentConditionEntry[] }) =>
    request<SegmentPreview>('/segments/preview', { method: 'POST', body: JSON.stringify(body) }),

  savedViews: () => request<SavedViewsResponse>('/saved-views'),

  createSavedView: (body: {
    name: string
    shared: boolean
    match: 'all' | 'any'
    conditions: SegmentConditionEntry[]
    icon?: string | null
    teamId?: string | null
  }) => request<{ ok: true; id: string }>('/saved-views', { method: 'POST', body: JSON.stringify(body) }),

  updateSavedView: (
    id: string,
    body: {
      name?: string
      match?: 'all' | 'any'
      conditions?: SegmentConditionEntry[]
      sortOrder?: number
      icon?: string | null
      teamId?: string | null
    },
  ) => request<{ ok: true }>(`/saved-views/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  deleteSavedView: (id: string) => request<{ ok: true }>(`/saved-views/${id}`, { method: 'DELETE' }),

  contacts: (params?: { q?: string; segmentId?: string }) => {
    const qs = new URLSearchParams()
    if (params?.q) qs.set('q', params.q)
    if (params?.segmentId) qs.set('segmentId', params.segmentId)
    const suffix = qs.toString()
    return request<ContactsResponse>(`/contacts${suffix ? `?${suffix}` : ''}`)
  },

  tagContacts: (body: { contactIds: string[]; tag: string; mode?: 'add' | 'remove' }) =>
    request<{ ok: true; updated: number }>('/contacts/tag', { method: 'POST', body: JSON.stringify(body) }),

  setContactsOptedOut: (body: { contactIds: string[]; optedOut: boolean }) =>
    request<{ ok: true; updated: number }>('/contacts/opt-out', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  setContactsCompany: (body: { contactIds: string[]; company: string | null }) =>
    request<{ ok: true; updated: number }>('/contacts/company', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  tasks: (params?: { conversationId?: string; status?: string; assignee?: string }) => {
    const qs = new URLSearchParams()
    if (params?.conversationId) qs.set('conversationId', params.conversationId)
    if (params?.status) qs.set('status', params.status)
    if (params?.assignee) qs.set('assignee', params.assignee)
    const suffix = qs.toString()
    return request<TasksResponse>(`/tasks${suffix ? `?${suffix}` : ''}`)
  },

  createTask: (body: TaskInput) =>
    request<{ ok: true; id: string }>('/tasks', { method: 'POST', body: JSON.stringify(body) }),

  updateTask: (id: string, body: Partial<TaskInput>) =>
    request<{ ok: true }>(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

  completeTask: (id: string) => request<{ ok: true }>(`/tasks/${id}/complete`, { method: 'POST' }),

  reopenTask: (id: string) => request<{ ok: true }>(`/tasks/${id}/reopen`, { method: 'POST' }),

  deleteTask: (id: string) => request<{ ok: true }>(`/tasks/${id}`, { method: 'DELETE' }),

  notifications: (unreadOnly?: boolean) =>
    request<{ items: NotificationView[] }>(`/notifications${unreadOnly ? '?unread=1' : ''}`),

  markNotificationRead: (id: string) => request<{ ok: true }>(`/notifications/${id}/read`, { method: 'POST' }),

  markAllNotificationsRead: () => request<{ ok: true }>('/notifications/read-all', { method: 'POST' }),
}

/** The browser previews from the local file, so this is only needed for a stored attachment. */
export function mediaUrl(key: string): string {
  return `/api/media/${key.split('/').pop() ?? ''}`
}

export interface AutoReplyResponse {
  config: AutoReplyConfig
  activity: {
    id: string
    kind: string | null
    sent: boolean
    detail: string | null
    createdAt: string
  }[]
  knowledgeCount: number
  aiConfigured: boolean
  canManage: boolean
}

export interface KnowledgeInput {
  title: string
  body: string
  keywords: string[]
  enabled: boolean
}

export interface KnowledgeAnswerView extends KnowledgeInput {
  id: string
  updatedAt: string
  /** Whether this answer can be found by meaning as well as by words. */
  indexed: boolean
}

export interface AiSettingsView {
  configured: boolean
  baseUrl: string
  model: string
  canManage: boolean
}

export interface AuditLogEntry {
  id: string
  seq: number
  actorUserId: string | null
  actorName: string | null
  actorType: 'user' | 'system'
  action: string
  entityType: string | null
  entityId: string | null
  summary: string
  metadata: Record<string, unknown>
  prevHash: string
  hash: string
  createdAt: string
}

export interface AuditLogResponse {
  entries: AuditLogEntry[]
  retentionDays: number | null
}

export interface AuditLogVerification {
  valid: boolean
  totalRows: number
  brokenAtSeq: number | null
  reason: string | null
}

export interface SsoSettingsView {
  issuer: string | null
  clientId: string | null
  hasClientSecret: boolean
  allowedDomain: string | null
  /** The DNS TXT record name to publish, e.g. "_wami-verify.example.com". Null until a domain
   *  has been saved. */
  domainVerificationRecord: string | null
  /** The value to publish at that name. Null once verified — nothing left to prove, nothing
   *  left to display. */
  domainVerificationToken: string | null
  domainVerifiedAt: string | null
  enabled: boolean
  loginUrl: string
  redirectUri: string
  scimUrl: string
  scimTokenDisplayHint: string | null
  scimTokenCreatedAt: string | null
}

export interface KnowledgeDocumentView {
  documentKey: string
  documentName: string
  chunks: number
  /** How many of this document's chunks are searchable by meaning, not just by word. */
  indexed: number
}

export interface KnowledgeResponse {
  answers: KnowledgeAnswerView[]
  documents: KnowledgeDocumentView[]
  gaps: { phrase: string; count: number; lastSeen: string; examples: string[] }[]
  model: { url: string; model: string } | null
  canManage: boolean
}

export interface KnowledgeAnswer {
  hits: {
    id: string
    title: string
    body: string
    score: number
    similarity: number | null
    via: ('keywords' | 'meaning')[]
    matched: string[]
  }[]
  confidence: 'none' | 'weak' | 'likely' | 'strong'
  usedModel: boolean
}

export interface ReactionRulesResponse {
  rules: { id: string; emoji: string; action: string; value: string | null; enabled: boolean }[]
  actions: Record<string, string>
  connected: number
  canManage: boolean
}

export interface GroupSummary {
  jid: string
  subject: string
  size: number
  announceOnly: boolean
  locked: boolean
  admins: number
}

export interface GroupDetail extends GroupSummary {
  description: string | null
  owner: string | null
  createdAt: number | null
  participants: { jid: string; phone: string; admin: 'admin' | 'superadmin' | null }[]
}

export interface GroupsResponse {
  groups: GroupSummary[]
  numbers: { id: string; phone: string; label: string }[]
  numberId?: string
  canManage?: boolean
  masked?: boolean
  error?: string
}

export interface GroupDetailResponse {
  group: GroupDetail
  canManage: boolean
  masked: boolean
}

export interface GroupActionInput {
  action:
    | 'add'
    | 'remove'
    | 'promote'
    | 'demote'
    | 'subject'
    | 'description'
    | 'announcement'
    | 'not_announcement'
    | 'locked'
    | 'unlocked'
    | 'invite-link'
    | 'revoke-link'
    | 'leave'
  participants?: string[]
  value?: string
  numberId?: string
}

export interface GroupBlueprint {
  id: string
  industries: string[]
  name: string
  subject: string
  purpose: string
  description: string
  announceOnly: boolean
  caution: string | null
  polls: { question: string; options: string[]; selectableCount: number }[]
}

/** Which parts of a blueprint actually stuck. Reported separately because the group exists
 *  either way, and a notice board that quietly stayed a discussion group is worth knowing. */
export interface CreatedFromBlueprint {
  id: string
  applied: { description: boolean; announceOnly: boolean }
  polls: { question: string; options: string[]; selectableCount: number }[]
}

export interface ParticipantActivityView {
  phone: string | null
  admin: boolean
  messages: number
  lastMessageAt: string | null
  joinedAt: string | null
  leftAt: string | null
  pollsAsked: number
  pollsAnswered: number
  answerRate: number | null
  state: 'left' | 'silent' | 'answers_only' | 'active'
}

export interface GroupAnalyticsResponse {
  conversationId: string
  subject: string | null
  windowDays: number
  /** False when the live group could not be read; history is still reported. */
  rosterKnown: boolean
  participants: ParticipantActivityView[]
  pulse: {
    members: number
    active: number
    silent: number
    silentShare: number
    messages: number
    messagesPerDay: number
    speakersEarlier: number
    speakersRecent: number
    answerRate: number | null
    verdict: 'healthy' | 'thinning' | 'failing' | 'dormant'
    reason: string
  }
}

export type RewriteStyle = 'professional' | 'friendly' | 'concise' | 'proofread'

export interface GroupsHealthResponse {
  windowDays: number
  groups: {
    jid: string
    subject: string | null
    verdict: 'healthy' | 'thinning' | 'failing' | 'dormant'
    reason: string
    silentShare: number
    messagesPerDay: number
  }[]
}

export interface PollView {
  id: string
  question: string
  options: string[]
  selectableCount: number
  closedAt: string | null
  createdAt: string
  results: {
    options: { option: string; votes: number; share: number; voters: string[] }[]
    voters: number
    leading: string | null
    tied: boolean
    participation: { asked: number; answered: number; silent: string[]; turnout: number | null }
    sentence: string
  }
}

export interface PollsResponse {
  polls: PollView[]
  canManage: boolean
  masked: boolean
}

export interface WorkflowRuleView {
  id: string
  name: string
  priorityOrder: number
  keywords: string[]
  appliesTo: 'direct' | 'group' | null
  category: string
  ticketPriority: 'low' | 'normal' | 'high' | 'urgent'
  slaMinutes: number
  autoTags: string[]
  enabled: boolean
}

export type WorkflowRuleInput = Omit<WorkflowRuleView, 'id' | 'priorityOrder'>

export interface WorkflowRulesResponse {
  rules: WorkflowRuleView[]
  problems: RuleProblem[]
  categories: string[]
  canManage: boolean
}

export interface IntegrationConnection {
  id: string
  provider: string
  status: 'connected' | 'error' | 'disabled'
  options: Record<string, string>
  events: string[]
  lastEventAt: string | null
  lastError: string | null
  createdAt: string
  counts: Record<string, number>
}

export interface IntegrationsResponse {
  catalogue: IntegrationDefinition[]
  connections: IntegrationConnection[]
  activity: {
    id: string
    integrationId: string
    type: string
    status: string
    attempts: number
    result: string | null
    lastError: string | null
    nextAttemptAt: string
    createdAt: string
  }[]
  canManage: boolean
}

export interface InboundWebhookSettings {
  url: string
  displayHint: string | null
  rotatedAt: string | null
}

export interface UploadedMedia {
  key: string
  kind: 'image' | 'video' | 'audio' | 'document'
  mime: string
  fileName: string
  size: number
}

/**
 * Header values must be Latin-1. A filename in any other script would otherwise throw before
 * the request is even sent, so it is percent-encoded and decoded server side.
 */
function encodeHeader(value: string): string {
  // eslint-disable-next-line no-control-regex
  return /^[\u0000-\u00ff]*$/.test(value) ? value : encodeURIComponent(value)
}

export interface DuplicateBroadcastSource {
  name: string
  body: string
  variables: { name: string; fallback: string }[]
  recipients: { remoteId: string; displayName: string | null; variables: Record<string, string> }[]
  media: { key: string; kind: 'image' | 'video' | 'audio' | 'document'; mime: string; fileName: string; size: number } | null
}

export interface BroadcastsResponse {
  broadcasts: {
    id: string
    name: string
    body: string
    status: string
    scheduledFor: string | null
    recurrence: string | null
    recurrenceInterval: number | null
    recurrenceEndAt: string | null
    recurrenceEndCount: number | null
    recurrenceWeekdays: number[] | null
    occurrenceIndex: number
    createdAt: string
    /** Set when an agent credential started the send rather than a teammate. */
    createdByAgentId: string | null
    /** The agent's own label for which touch of a campaign this is. */
    touchRef: string | null
    counts: Record<string, number>
  }[]
  savedLists: { id: string; name: string; entries: { remoteId: string; displayName?: string }[] }[]
  templates: { id: string; name: string; body: string; media: UploadedMedia | null }[]
  numbers: { id: string; phone: string; label: string; maturity: string }[]
  credits: { monthlyRemaining: number; topupRemaining: number; allowance: number; used: number }
  canSend: boolean
}

export interface BroadcastDetail {
  broadcast: BroadcastsResponse['broadcasts'][number]
  recipients: {
    id: string
    remoteId: string
    displayName: string | null
    channelNumber: string | null
    status: 'pending' | 'sent' | 'failed' | 'deferred' | 'skipped'
    error: string | null
    sentAt: string | null
    attempts: number
  }[]
}

export interface ReachabilityReport {
  total: number
  reachable: number
  unreachable: number
  unknown: number
  sentence: string
}

export interface BroadcastPreview {
  recipients: number
  scheduled: number
  deferred: number
  /** Recipients dropped because WhatsApp has already said they have no account. */
  unreachable: number
  reachability: ReachabilityReport
  estimatedMinutes: number
  perNumber: {
    numberId: string
    label: string
    phone?: string
    maturity: string
    count: number
    cap: number
    warning: string | null
    warmup: { day: number; rampCap: number; cap: number; complete: boolean; reason: string | null } | null
  }[]
  credits: {
    monthlyRemaining: number
    topupRemaining: number
    spend: { fromMonthly: number; fromTopup: number; short: number }
  }
  /** How exposed this send is if a number drops part-way through it. */
  fleet: {
    sendable: number
    withRoom: number
    capacityToday: number
    hasBackup: boolean
    warning: string | null
  }
  /** Capped server-side at 20 — `missingVariableCount` is the true total. */
  missingVariables: { recipientId: string; variable: string }[]
  missingVariableCount: number
  sample: string | null
  blockers: string[]
}

export interface MetricsResponse {
  volume: { conversations: number; unread: number; messagesOut: number; messagesIn: number }
  messageVolume: { date: string; incoming: number; outgoing: number; flagged: number }[]
  tickets: {
    open: number
    breaching: number
    unassigned: number
    closed: number
    medianFirstResponseMinutes: number | null
    answeredWithinTarget: number
    answered: number
    resolutionBuckets: { underHour: number; underDay: number; underWeek: number; overWeek: number }
  }
  flagged: {
    count: number
    medianResponseMinutes: number | null
    answered: number
  }
  categories: { name: string; total: number; breaching: number }[]
  labels: { name: string; total: number; breaching: number }[]
  history: { date: string; created: number; closed: number; unresolved: number }[]
  agents: {
    userId: string
    name: string
    sent: number
    notes: number
    openTickets: number
    closedTickets: number
    avgResolutionMinutes: number | null
    medianFirstResponseMinutes: number | null
  }[]
  scoped: boolean
  filters: { applied: boolean; match: 'all' | 'any' }
}

export interface PropertyDefView {
  id: string
  entity: 'chat' | 'ticket'
  name: string
  type: string
  section: string
  options: string[]
  cascadeOptions: string[][]
  hint: string | null
  requiredBeforeClose: boolean
}

export interface PropertySectionView {
  id: string
  entity: 'chat' | 'ticket'
  name: string
  displayOrder: number
}

export interface PropertyView {
  id: string
  name: string
  type: 'text' | 'date' | 'single_select' | 'multi_select' | 'number' | 'file' | 'cascading_select'
  section: string
  options: string[]
  cascadeOptions: string[][]
  hint: string | null
  requiredBeforeClose: boolean
  value: string | null
}

export interface ShiftSlotView {
  weekday: number
  startMinute: number
  endMinute: number
  timezone: string
}

export interface TeamMemberView {
  userId: string
  name: string
  email: string
  role: 'owner' | 'admin' | 'agent' | 'viewer'
  joinedAt: string
  unrestricted: boolean
  labelIds: string[]
  numberIds: string[]
  presence: 'online' | 'offline'
  presenceUpdatedAt: string | null
  shifts: ShiftSlotView[]
}

export interface TeamGroup {
  id: string
  name: string
  memberIds: string[]
}

export interface TeamResponse {
  teams: TeamGroup[]
  members: TeamMemberView[]
  invitations: {
    id: string
    email: string
    role: string
    labelIds: string[]
    expiresAt: string
  }[]
  labels: { id: string; name: string; color: string }[]
  numbers: { id: string; label: string; phone: string }[]
  seats: { used: number; total: number }
  canManage: boolean
  maskNumbers: boolean
  revealDeletedMessages: boolean
  restrictOutgoingNumber: boolean
  hideAgentName: boolean
}

export interface IdentityLinkView {
  key: 'agent' | 'operator' | 'business' | 'representative'
  label: string
  question: string
  status: 'verified' | 'claimed'
  basis: string
}

/** The owner's pause switch and hours for agent-started sends — `SendWindow` in the worker. */
export interface AgentSendSettings {
  paused: boolean
  startMinute: number
  endMinute: number
  days: number[]
  timezone: string
}

export interface AgentCredentialView {
  id: string
  name: string
  operatorClaim: string | null
  operatorVersion: string | null
  scopes: string[]
  displayHint: string
  lastUsedAt: string | null
  revokedAt: string | null
  createdAt: string
  issuedByName: string
  /** True only when the customer explicitly granted the whole workspace. */
  unrestricted: boolean
  labelIds: string[]
  chain: IdentityLinkView[]
}

export interface AgentLabel {
  id: string
  name: string
  color: string
}

export interface AgentsResponse {
  credentials: AgentCredentialView[]
  labels: AgentLabel[]
  scopes: { id: string; label: string; description: string; write: boolean }[]
  enabled: boolean
  endpoint: string
}

/** The only response that ever carries the token, and only at the moment of creation. */
export interface NewAgentCredential {
  id: string
  name: string
  scopes: string[]
  displayHint: string
  token: string
}

export interface AgentAction {
  id: string
  credentialId: string
  credentialName: string
  operatorClaim: string | null
  tool: string
  outcome: 'ok' | 'denied' | 'error'
  detail: string | null
  affected: number | null
  durationMs: number | null
  createdAt: string
}

export interface TenantResponse {
  tenant: {
    status: 'pending' | 'provisioning' | 'ready' | 'failed' | 'revoked'
    ownership: 'customer' | 'platform'
    provider: 'neon' | 'supabase' | 'local'
    region: string | null
    neonProjectId: string | null
    failureReason: string | null
    readyAt: string | null
  } | null
  regions: readonly { id: string; label: string; hint: string }[]
  oauthAvailable: boolean
  managedAvailable: boolean
}

export interface InboxConversation {
  id: string
  channelNumber: string
  remoteId: string
  kind: 'direct' | 'group'
  subject: string | null
  /**
   * The person behind a 1:1 chat. Null for a group, which carries its own subject, and null
   * for a direct chat WhatsApp gave no push name for — see `conversationTitle`.
   */
  contactName: string | null
  lastMessageAt: string | null
  lastMessagePreview: string | null
  unreadCount: number
  /** Only populated on the inbox list; the single-conversation route omits it. */
  labels?: string[]
  /** Assigns the chat itself, independent of any ticket on it. */
  assignedTo: string | null
  /** The team tier above `assignedTo` — set with or without an individual assignee. */
  assignedTeamId: string | null
  /** WhatsApp's delivery status for this row's own last outbound send — null when the last
   *  message was inbound, a private note, or deleted. Only populated on the inbox list. */
  lastOutboundAck?: 'sent' | 'delivered' | 'read' | 'failed' | null
  /** Null for a group, or a direct chat with no linked contact record — only populated on the
   *  single-conversation route, same as contactName. */
  contactOptedOut?: boolean | null
  /** A ready-to-show sentence when this thread's own number can't currently send — null when
   *  it's healthy. Only populated on the single-conversation route. */
  numberIssue?: string | null
  /** This chat's override of the workspace's AI-auto-reply setting. Null means no override —
   *  see `AiResponderState` in `@shared/autoReply`. */
  aiResponderState?: 'active' | 'thinking' | 'snoozed' | 'inactive' | null
}

export interface InboxTicket {
  id: string
  reference: string
  conversationId: string
  title: string
  category: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  status: 'open' | 'pending' | 'resolved' | 'closed'
  assigneeUserId: string | null
  dueAt: string | null
  firstResponseAt: string | null
  matchedRule: string | null
  breached: boolean
}

export interface InboxResponse {
  conversations: InboxConversation[]
  tickets: InboxTicket[]
  categories: string[]
  /** True when the caller's role has customer numbers hidden from it. */
  masked?: boolean
  scoped?: boolean
}

export type MessageAction =
  | 'edit'
  | 'delete'
  | 'delete-me'
  | 'pin'
  | 'unpin'
  | 'react'
  | 'flag'
  | 'unflag'
  | 'attach-ticket'
  | 'detach-ticket'
  | 'create-ticket'
  | 'mark-internal'
  | 'unmark-internal'

export interface ConversationMessage {
  id: string
  direction: 'inbound' | 'outbound'
  senderName: string | null
  senderPhone: string | null
  body: string | null
  /**
   * What kind of thing arrived, when it was not words. `mediaUrl` is an R2 key rather than a
   * URL despite the name — pass it through `mediaUrl()` to get something fetchable. Both are
   * null for a sticker or a pin: nothing was downloaded, only labelled. See HANDOVER §8.
   */
  mediaType: MessageMediaType | null
  mediaUrl: string | null
  isPrivateNote: boolean
  sentAt: string
  /** WhatsApp's own id. Null for a private note, or an outbound message sent before this
   *  field existed — either way, nothing to key an edit/delete/pin/react action off. */
  externalId: string | null
  /**
   * WhatsApp's ticks for something we sent, pushed by the session host as they happen.
   *
   * Null on every inbound message, on a private note, and on anything sent while the number
   * was not connected — so a missing ack is not a failed send, it is an unheard-of one.
   */
  ack: 'sent' | 'delivered' | 'read' | 'failed' | null
  editedAt: string | null
  deletedAt: string | null
  deletedFor: 'everyone' | 'me' | null
  pinnedAt: string | null
  reactions: string[]
  flaggedAt: string | null
  flagReason: string | null
  /** A ticket this message was manually attached to, distinct from ticket auto-routing. */
  ticketId: string | null
  /** Retroactively hidden from customer-facing surfaces without being deleted. */
  markedInternal: boolean
}

export interface TicketEvent {
  id: string
  ticketId: string
  type: 'created' | 'assigned' | 'status_changed' | 'responded' | 'escalated' | 'note'
  actorUserId: string | null
  /** Already a complete, human-readable sentence written at insert time — rendered as-is. */
  detail: string | null
  createdAt: string
}

export interface ConversationResponse {
  conversation: InboxConversation
  messages: ConversationMessage[]
  tickets: InboxTicket[]
  /** Assignment/resolution/reply-latency events for this conversation's ticket(s), merged
   *  inline into the message timeline. Empty for a chat with no ticket. */
  events: TicketEvent[]
  masked?: boolean
}

export interface ScheduledMessageView {
  id: string
  conversationId: string
  body: string
  scheduledFor: string
  recurrence: 'daily' | 'weekly' | 'monthly' | null
  recurrenceInterval: number | null
  recurrenceEndAt: string | null
  recurrenceEndCount: number | null
  recurrenceWeekdays: number[] | null
  occurrenceIndex: number
  seriesId: string | null
  status: 'pending' | 'sending' | 'sent' | 'cancelled' | 'failed'
  error: string | null
  createdAt: string
}

export interface ScheduledMessageLogView extends ScheduledMessageView {
  conversationSubject: string | null
}

export interface SegmentCondition {
  field: string
  operator: Operator
  value?: string
}

/** One level of nesting — a group of conditions with its own match mode. Distinguished from a
 *  `SegmentCondition` by carrying a `conditions` array, mirroring `@shared/segments`. */
export interface SegmentConditionGroup {
  match: 'all' | 'any'
  conditions: SegmentCondition[]
}

export type SegmentConditionEntry = SegmentCondition | SegmentConditionGroup

export interface SegmentView {
  id: string
  name: string
  entity: 'contact' | 'conversation'
  match: 'all' | 'any'
  conditions: SegmentConditionEntry[]
  description: string
  createdAt: string
}

export interface SegmentsResponse {
  segments: SegmentView[]
  canManage: boolean
}

export interface SegmentPreview {
  count: number
  description: string
  problems: string[]
  sample: { id: string; name?: string | null; phone?: string; subject?: string | null; remoteId?: string }[]
}

export interface SavedView {
  id: string
  name: string
  segment: { match: 'all' | 'any'; conditions: SegmentConditionEntry[] }
  shared: boolean
  ownerUserId: string | null
  icon: string | null
  teamId: string | null
  sortOrder: number
  mine: boolean
  createdAt: string
}

export interface SavedViewsResponse {
  views: SavedView[]
  canShare: boolean
}

export interface ContactView {
  id: string
  name: string | null
  phone: string
  company: string | null
  tags: string[]
  optedOut: boolean
  createdAt: string
  lastMessageAt: string | null
  messageCount: number
  openTickets: number
  labels: string[]
}

export interface ContactsResponse {
  contacts: ContactView[]
  total: number
  appliedSegment: { id: string; name: string } | null
  masked: boolean
  canManage: boolean
  scoped: boolean
}

export interface TaskView {
  id: string
  title: string
  notes: string | null
  status: 'open' | 'done' | 'cancelled'
  priority: 'low' | 'normal' | 'high' | 'urgent'
  assigneeUserId: string | null
  dueAt: string | null
  remindAt: string | null
  conversationId: string | null
  messageId: string | null
  ticketId: string | null
  completedAt: string | null
  completedByUserId: string | null
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
  overdue: boolean
}

export interface TaskInput {
  title: string
  notes?: string | null
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  assigneeUserId?: string | null
  dueAt?: string | null
  remindAt?: string | null
  conversationId?: string | null
  messageId?: string | null
  ticketId?: string | null
}

export interface TasksResponse {
  tasks: TaskView[]
  counts: { open: number; overdue: number; dueSoon: number }
  scoped: boolean
}

export interface NotificationView {
  id: string
  userId: string
  type: 'ticket_assigned' | 'chat_assigned' | 'task_assigned' | 'ticket_escalated' | 'task_reminder' | 'automation' | 'number_health'
  title: string
  body: string | null
  entityType: 'ticket' | 'task' | 'conversation' | null
  entityId: string | null
  readAt: string | null
  createdAt: string
}

export interface LabelView {
  id: string
  name: string
  color: string
  scope?: 'chat' | 'number'
  description?: string | null
}

export interface TicketStatusLabel {
  kind: 'open' | 'pending' | 'resolved' | 'closed'
  label: string
  color: string
}

export interface TicketStatusesView {
  statuses: TicketStatusLabel[]
  canManage: boolean
}

/** Matches the server's LABEL_PALETTE so a picked swatch round-trips exactly. */
export const LABEL_PALETTE = [
  '#0b6b62', '#2e7d32', '#0277bd', '#5e35b1', '#ad1457',
  '#c62828', '#d84315', '#ef6c00', '#f9a825', '#616161',
  '#00838f', '#4e342e', '#6d4c41', '#455a64',
]

export interface QuickReplyView {
  id: string
  shortcut: string
  title: string
  body: string
  visibility: 'all' | 'members' | 'roles'
  visibilityIds: string[]
}

export interface AutomationRuleView {
  id: string
  name: string
  trigger: AutomationTrigger
  match: 'all' | 'any'
  conditions: SegmentConditionEntry[]
  actions: AutomationAction[]
  enabled: boolean
  priorityOrder: number
  /** Wait this long after the trigger before checking conditions, instead of immediately. */
  delaySeconds: number | null
  createdAt: string
  updatedAt: string
}

export interface AutomationRuleInput {
  name: string
  trigger: AutomationTrigger
  match: 'all' | 'any'
  conditions: SegmentConditionEntry[]
  actions: AutomationAction[]
  enabled: boolean
  delaySeconds?: number | null
}

export interface AutomationRulesResponse {
  rules: AutomationRuleView[]
  canManage: boolean
}

/** A flow's step is a full automation rule under the hood — same shape as AutomationRuleView,
 *  just always carrying flowId/stepOrder. */
export interface AutomationFlowStep extends AutomationRuleView {
  flowId: string
  stepOrder: number
}

export interface AutomationFlowView {
  id: string
  name: string
  description: string | null
  steps: AutomationFlowStep[]
  createdAt: string
  updatedAt: string
}

export interface AutomationFlowStepInput {
  /** Defaults to the flow's own name when left blank — most steps don't need their own. */
  name?: string
  match: 'all' | 'any'
  conditions: SegmentConditionEntry[]
  actions: AutomationAction[]
  enabled: boolean
  /** Minutes to wait after the trigger before this step checks its conditions. 0 or omitted
   *  means check immediately, the same as an ordinary rule with no delaySeconds. */
  delayMinutes?: number
}

export interface AutomationFlowInput {
  name: string
  description?: string | null
  trigger: AutomationTrigger
  steps: AutomationFlowStepInput[]
}

export interface AutomationFlowsResponse {
  flows: AutomationFlowView[]
  canManage: boolean
}

/** A product info card sent as an image+text WhatsApp message with a payment link — not a
 *  native WhatsApp catalog item, which needs Meta's Cloud API. See the worker's own note on this. */
export interface ProductView {
  id: string
  name: string
  description: string | null
  priceMinor: number
  currency: string
  mediaKey: string | null
  mediaKind: 'image' | 'video' | 'audio' | 'document' | null
  mediaMime: string | null
  mediaFileName: string | null
  mediaSize: number | null
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface ProductInput {
  name: string
  description?: string | null
  priceMinor: number
  currency: string
  active?: boolean
  media?: UploadedMedia | null
}

export interface ProductsResponse {
  products: ProductView[]
  canManage: boolean
}

export interface AutomationRuleRun {
  id: string
  ruleId: string
  trigger: AutomationTrigger
  matched: boolean
  actionsRun: { type: string; ok: boolean; detail: string | null }[]
  error: string | null
  createdAt: string
}
